import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Truck, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Draft","Submitted","Waiting for approval","Approved","Rejected","Picking","Packed","Sent","Delivered","Installed","Verified","Closed"];
const TONE = {
  "Draft": "bg-slate-100 text-slate-700", "Submitted": "bg-blue-100 text-blue-800",
  "Waiting for approval": "bg-amber-100 text-amber-800", "Approved": "bg-emerald-100 text-emerald-800",
  "Rejected": "bg-red-100 text-red-800", "Picking": "bg-indigo-100 text-indigo-800",
  "Packed": "bg-indigo-100 text-indigo-800", "Sent": "bg-cyan-100 text-cyan-800",
  "Delivered": "bg-teal-100 text-teal-800", "Installed": "bg-lime-100 text-lime-800",
  "Verified": "bg-emerald-100 text-emerald-800", "Closed": "bg-slate-200 text-slate-700",
};

export default function POSReport() {
  const [data, setData] = useState(null);
  const [txns, setTxns] = useState([]);
  const [f, setF] = useState({ status: "", state: "", priority: "" });

  const load = () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/pos-report", { params }).then(r => setData(r.data));
    api.get("/pos-stock-transactions").then(r => setTxns(r.data)).catch(() => {});
  };
  useEffect(load, [f]);

  const doExport = async (fmt) => {
    try {
      const res = await api.get("/reports/pos_requests", { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `MLILY-POS-Report.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export not yet available for POS — use Excel from source page"); }
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="pos-report-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight flex items-center gap-2"><Truck className="w-8 h-8 text-[#003DA5]" /> POS Materials Report</h1>
          <p className="text-slate-600 mt-1">Manager view — request pipeline, warehouse stock, transactions</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-2">
        <select value={f.status} onChange={e => setF({ ...f, status: e.target.value })} className="h-10 px-3 border rounded-lg text-sm" data-testid="rep-filter-status">
          <option value="">All statuses</option>{STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={f.state} onChange={e => setF({ ...f, state: e.target.value })} className="h-10 px-3 border rounded-lg text-sm" data-testid="rep-filter-state">
          <option value="">All states</option>{["NSW","VIC","QLD","SA","WA","TAS","NT","ACT"].map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })} className="h-10 px-3 border rounded-lg text-sm" data-testid="rep-filter-priority">
          <option value="">Any priority</option>
          <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
        </select>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {STATUSES.map(s => (
          <div key={s} className="bg-white p-3 rounded-lg border border-slate-200" data-testid={`rep-kpi-${s.replace(/\s/g, "-")}`}>
            <div className="text-[10px] uppercase font-semibold text-slate-500">{s}</div>
            <div className="text-xl font-heading font-bold text-[#001F54]">{data.by_status[s] || 0}</div>
          </div>
        ))}
      </div>

      {data.low_stock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="low-stock-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm flex-1">
              <div className="font-semibold text-amber-900">{data.low_stock.length} materials below reorder level</div>
              <ul className="text-amber-800 mt-2 grid grid-cols-1 md:grid-cols-2 gap-1">
                {data.low_stock.map(m => <li key={m.id}>{m.name}: <b>{m.warehouse_stock}</b> / {m.reorder_level}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b font-heading font-semibold text-[#001F54]">Requests ({data.requests.length})</div>
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2">Store</th>
                <th className="text-left px-4 py-2">State</th>
                <th className="text-left px-4 py-2">Agent</th>
                <th className="text-left px-4 py-2">Reason</th>
                <th className="text-left px-4 py-2">Items</th>
                <th className="text-left px-4 py-2">Priority</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {data.requests.map(r => (
                <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`rep-row-${r.id}`}>
                  <td className="px-4 py-2 font-medium"><Link to={`/accounts/${r.customer_id}`} className="text-[#003DA5] hover:underline">{r.customer_name}</Link></td>
                  <td className="px-4 py-2 text-xs">{r.state}</td>
                  <td className="px-4 py-2 text-xs">{r.agent_name || "—"}</td>
                  <td className="px-4 py-2 text-xs">{r.reason}</td>
                  <td className="px-4 py-2">{r.items.length}</td>
                  <td className="px-4 py-2 text-xs">{r.priority}</td>
                  <td className="px-4 py-2"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${TONE[r.status] || "bg-slate-100"}`}>{r.status}</span></td>
                  <td className="px-4 py-2 text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {data.requests.length === 0 && <tr><td colSpan="8" className="px-4 py-12 text-center text-slate-500">No requests.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b font-heading font-semibold text-[#001F54]">Warehouse Stock ({data.materials.length})</div>
        <table className="w-full text-sm">
          <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Material</th>
              <th className="text-left px-4 py-2">Code</th>
              <th className="text-left px-4 py-2">Stock</th>
              <th className="text-left px-4 py-2">Reorder</th>
              <th className="text-left px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.materials.map(m => {
              const lo = (m.warehouse_stock || 0) < (m.reorder_level || 0);
              const neg = (m.warehouse_stock || 0) < 0;
              return (
                <tr key={m.id} className="border-t" data-testid={`rep-mat-${m.id}`}>
                  <td className="px-4 py-2 font-medium">{m.name}</td>
                  <td className="px-4 py-2 text-xs font-mono text-slate-500">{m.code}</td>
                  <td className={`px-4 py-2 font-semibold ${neg ? "text-red-600" : lo ? "text-amber-600" : "text-slate-700"}`}>{m.warehouse_stock}{neg && " ⚠"}</td>
                  <td className="px-4 py-2 text-xs">{m.reorder_level}</td>
                  <td className="px-4 py-2">{neg ? <span className="text-xs text-red-700 font-bold">NEGATIVE</span> : lo ? <span className="text-xs text-amber-700 font-bold">LOW</span> : <span className="text-xs text-emerald-700 font-bold">OK</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {txns.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b font-heading font-semibold text-[#001F54]">Stock Transactions ({txns.length})</div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500 sticky top-0">
                <tr><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Material</th><th className="text-left px-4 py-2">Delta</th><th className="text-left px-4 py-2">For store</th><th className="text-left px-4 py-2">By</th></tr>
              </thead>
              <tbody>
                {txns.map(t => (
                  <tr key={t.id} className="border-t" data-testid={`rep-txn-${t.id}`}>
                    <td className="px-4 py-2 text-xs">{new Date(t.at).toLocaleString()}</td>
                    <td className="px-4 py-2">{t.material_name}</td>
                    <td className={`px-4 py-2 font-bold ${t.delta < 0 ? "text-red-600" : "text-emerald-600"}`}>{t.delta > 0 ? "+" : ""}{t.delta}</td>
                    <td className="px-4 py-2 text-xs">{t.customer_name || "—"}</td>
                    <td className="px-4 py-2 text-xs">{t.by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
